import React, { useState, useEffect, useRef } from 'react';
import { getTaskById } from '../../config/taskRegistry';
import { apiGet, apiPost } from '../../utils/api';
import DynamicHeader from '../common/DynamicHeader';
import { X, User, LogOut, Home, Calendar, FileText, MessageSquare, Settings, Bell, Info } from 'lucide-react';
import { Button, Card, Spinner, Badge, CloseButton } from '../design-system';
import TaskCompletionCelebration from './TaskCompletionCelebration';
import analyticsService from '../../services/analyticsService';
import { useAuth } from '../../contexts/AuthContext';

const TaskPage = ({ taskId, onBack, onViewCards }) => {
  const { logout } = useAuth();
  const [task, setTask] = useState(null);
  const [taskType, setTaskType] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const taskComponentRef = useRef(null);

  // Navigation menu items - same as PatientDashboard
  const menuItems = [
    { id: 'dashboard', name: 'Dashboard', icon: Home, active: true },
    { id: 'appointments', name: 'Appointments', icon: Calendar },
    { id: 'tasks', name: 'My Tasks', icon: FileText },
    { id: 'therapist', name: 'About Therapist', icon: User },
    { id: 'chat', name: 'AI Chat', icon: MessageSquare },
    { id: 'settings', name: 'Settings', icon: Settings },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'help', name: 'Help & Support', icon: Info },
  ];

  const handleLogout = () => {
    logout();
  };

  const navigateToSection = (sectionId) => {
    if (sectionId === 'dashboard') {
      onBack(); // Go back to dashboard
    } else {
      // For other sections, you could implement navigation or just go back
      onBack();
    }
  };

  useEffect(() => {
    const loadTaskData = async () => {
      try {
        setLoading(true);
        
        // Get task details first using API helper
        const taskData = await apiGet(`/.netlify/functions/get-my-tasks?taskId=${taskId}`);
        const currentTask = taskData.tasks.find(t => t.taskId === taskId);
        
        if (!currentTask) {
          throw new Error('Task not found');
        }

        setTask(currentTask);

        // Get task type from new registry
        const taskTypeConfig = getTaskById(currentTask.taskTypeId);
        if (!taskTypeConfig) {
          throw new Error('Task type not found');
        }

        setTaskType(taskTypeConfig);
        
        // Start or get task session using API helper
        const sessionResult = await apiPost('/.netlify/functions/start-task-session', { taskId });
        if (sessionResult && sessionResult.session) {
          setSession(sessionResult.session);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTaskData();
  }, [taskId]);

  const handleTaskComplete = (additionalProperties = {}) => {
    // Track task completion event for all tasks (default tracking)
    // This ensures all tasks are tracked automatically, regardless of task type
    if (task && taskType && session) {
      try {
        // Map task type to readable name for analytics
        const taskTypeName = taskType.typeId || 'unknown';
        // Get category from taskType config
        const taskCategory = taskType.category || 'unknown';
        
        // Base properties that all tasks have
        const baseProperties = {
          taskId: task.taskId,
          taskType: taskTypeName,
          taskTypeId: taskType.typeId,
          taskCategory: taskCategory,
          taskTitle: task.title,
          sessionId: session.sessionId
        };
        
        // Merge with any additional properties from the specific task component
        analyticsService.track('task_completed', {
          ...baseProperties,
          ...additionalProperties
        });
      } catch (analyticsError) {
        // Don't break task completion if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
    }
    
    // Show celebration modal before navigating back
    setShowCelebration(true);
  };

  const handleCelebrationClose = () => {
    setShowCelebration(false);
    // Navigate back after celebration is closed
    onBack();
  };

  const handleHelpClick = () => {
    setShowHelp(true);
  };

  const closeHelp = () => {
    setShowHelp(false);
  };

  const handleBackToDashboard = () => {
    // Navigate back to the section the user came from
    onBack();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="large" />
          <p className="mt-4 text-gray-600">Loading task...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card variant="default" padding="large" className="text-center">
          <p className="text-red-600">Error: {error}</p>
          <Button 
            variant="primary"
            onClick={onBack}
            className="mt-4"
          >
            Back to Tasks
          </Button>
        </Card>
      </div>
    );
  }

  // Get the component to render from task type config
  const ComponentToRender = taskType.component;

  return (
    <>
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="flex h-full min-h-screen">
        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar Navigation */}
        <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-sm border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-4 md:p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-6 lg:mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 md:h-6 md:w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base md:text-lg font-semibold text-gray-900">Patient Portal</h2>
                  <p className="text-xs md:text-sm text-gray-500">Welcome back</p>
                </div>
              </div>
              <CloseButton
                onClick={() => setSidebarOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-700"
              />
            </div>

            {/* Navigation Menu */}
            <nav className="flex-1 space-y-1">
              {menuItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant={item.id === 'tasks' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => {
                      navigateToSection(item.id);
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-3 md:px-4 py-2 md:py-3 text-left"
                  >
                    <IconComponent className="h-4 w-4 md:h-5 md:w-5" />
                    <span className="font-medium text-sm md:text-base">{item.name}</span>
                  </Button>
                );
              })}
            </nav>

            {/* Logout Button */}
            <div className="mt-4 md:mt-8 pt-4 md:pt-6 border-t border-gray-200">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-3 md:px-4 py-2 md:py-3 text-left"
              >
                <LogOut className="h-4 w-4 md:h-5 md:w-5" />
                <span className="font-medium text-sm md:text-base">Logout</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col h-full min-w-0">
          {/* Header */}
          <DynamicHeader
            activeSection="task-execution"
            onMenuClick={() => setSidebarOpen(true)}
            onBackClick={handleBackToDashboard}
            onHelpClick={handleHelpClick}
            sectionNames={{
              'task-execution': taskType.pageTitle
            }}
            appName="TheraEngage"
            showMenuOnDashboard={false}
            showHelpIcon={true}
          />

          {/* Task Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <ComponentToRender
              ref={taskComponentRef}
              task={task}
              taskType={taskType}
              session={session}
              onComplete={handleTaskComplete}
            />
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card variant="elevated" padding="none" className="max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Task Information</h3>
              <CloseButton
                onClick={closeHelp}
                className="p-2 text-gray-400 hover:text-gray-600"
              />
            </div>
            <div className="p-4 space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Task Details</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Title:</span>
                    <span className="font-medium">{task.title || 'Untitled Task'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Type:</span>
                    <span className="font-medium">{taskType.pageTitle}</span>
                  </div>
                  {task.dueDate && (
                    <div className="flex justify-between">
                      <span>Due Date:</span>
                      <span className="font-medium">{new Date(task.dueDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {task.description && (
                    <div className="pt-2">
                      <span className="block text-gray-500 mb-1">Description:</span>
                      <p className="text-sm text-gray-700">{task.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Instructions</h4>
                <p className="text-sm text-gray-600">
                  {taskType.pageDescription || 'Complete this task according to the instructions provided.'}
                </p>
              </div>

            </div>
          </Card>
        </div>
      )}

      {/* Task Completion Celebration Modal */}
      <TaskCompletionCelebration 
        isOpen={showCelebration}
        onClose={handleCelebrationClose}
        onViewCards={onViewCards}
      />
    </>
  );
};

export default TaskPage; 